import { Card, MonoLabel, cn, focusRing } from '@/design-system';
import type { ReportSection } from '@/entities/report';

import { reportCopy } from '../copy';

export interface SectionListProps {
    sections: readonly ReportSection[];
    selectedUuid: string | null;
    onSelect: (section: ReportSection) => void;
}

/**
 * De sectielijst links: kiezen wat je in het documentvlak bekijkt en bijstelt.
 *
 * In de volgorde van de PDF en de klantpagina: wat er in de woning is gevonden
 * eerst, de bijlage "Toelichting en voorwaarden" achteraan, onder een eigen kop.
 */
export function SectionList({ sections, selectedUuid, onSelect }: SectionListProps) {
    const body = sections.filter((section) => !section.appendix);
    const appendix = sections.filter((section) => section.appendix);

    return (
        <Card padding="none" className="max-w-[236px] flex-[1_1_200px] p-3">
            <MonoLabel size={10} tone="muted" as="div" className="px-2 py-[6px]">
                {reportCopy.sectionsEyebrow}
            </MonoLabel>

            <Sections
                label={reportCopy.sectionsLabel}
                sections={body}
                selectedUuid={selectedUuid}
                onSelect={onSelect}
            />

            {appendix.length > 0 ? (
                <>
                    <MonoLabel
                        size={10}
                        tone="muted"
                        as="div"
                        className="mt-2 border-t border-[var(--border-subtle)] px-2 pt-3 pb-[6px]"
                    >
                        {reportCopy.sectionsAppendix}
                    </MonoLabel>
                    <Sections
                        label={reportCopy.sectionsAppendix}
                        sections={appendix}
                        selectedUuid={selectedUuid}
                        onSelect={onSelect}
                    />
                </>
            ) : null}

            <p className="m-0 mt-2 border-t border-[var(--border-subtle)] px-2 pt-3 pb-1 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {reportCopy.sectionsNote}
            </p>
        </Card>
    );
}

function Sections({
    label,
    sections,
    selectedUuid,
    onSelect,
}: {
    label: string;
    sections: readonly ReportSection[];
    selectedUuid: string | null;
    onSelect: (section: ReportSection) => void;
}) {
    return (
        <ul aria-label={label} className="m-0 list-none p-0">
            {sections.map((section) => {
                const active = section.uuid === selectedUuid;

                return (
                    <li key={section.uuid}>
                        <button
                            type="button"
                            aria-current={active ? 'true' : undefined}
                            onClick={() => onSelect(section)}
                            className={cn(
                                'flex min-h-[40px] w-full cursor-pointer items-center justify-between gap-2 rounded-[6px] border-0 px-2 py-[9px] text-left text-[13px]',
                                'font-[family-name:var(--font-body)]',
                                active
                                    ? 'bg-[var(--accent-tint)] font-medium text-[var(--accent-dark)]'
                                    : 'bg-transparent text-[var(--text-reading)] hover:bg-[var(--surface-row-hover)]',
                                focusRing,
                            )}
                        >
                            <span className="min-w-0 truncate">{section.label}</span>
                            <span className="flex shrink-0 items-center gap-[6px]">
                                {section.bodyIsGenerated ? null : (
                                    <MonoLabel size={9} tone="accent">
                                        {reportCopy.editedBadge}
                                    </MonoLabel>
                                )}
                                {section.pages ? (
                                    <MonoLabel size={10} variant="value" tone="muted">
                                        {section.pages}
                                    </MonoLabel>
                                ) : null}
                            </span>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}
